import DescriptionIcon from "@mui/icons-material/Description";
import HikingIcon from "@mui/icons-material/Hiking";
import { Box, IconButton, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { Dialog } from "components/Dialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { useMemo, useState } from "react";
import { getFacilityName } from "../../utils";
import { COMMUNICATION_REPORTS_QUERY } from "../hooks/useCommunicationReports";
import { CommunicationTierIIReport } from "../types";
import { useNavigateToFulfillmentReports } from "hooks/useNavigateToFulfillmentReports";

interface ReportsGridProps {
  facilityIds: string[];
  tenantId: string;
  initialReportIds: string[];
  close: () => void;
  saveReports: (reports: CommunicationTierIIReport[]) => void;
}

export const ReportsPickerDialog = ({
  facilityIds,
  tenantId,
  initialReportIds,
  close,
  saveReports,
}: ReportsGridProps) => {
  const [reports, setReports] = useState<CommunicationTierIIReport[]>([]);
  const [reportIds, setReportIds] = useState<string[]>(initialReportIds);
  const navigateToFulfillmentReports = useNavigateToFulfillmentReports({
    openInNewTab: true,
  });
  const columns: GridColDef<CommunicationTierIIReport>[] = useMemo(() => {
    return [
      {
        field: "facility",
        headerName: "Facility",
        flex: 1.5,
        minWidth: 200,
        valueGetter: (_, row) => {
          const facilityName = getFacilityName(row.facility);
          return facilityName;
        },
      },
      {
        field: "reportingYear",
        headerName: "Reporting Year",
        flex: 0.5,
        minWidth: 150,
      },
      {
        field: "reportKind",
        headerName: "Type",
        flex: 0.8,
        minWidth: 120,
        valueGetter: (_, row) => {
          return row.reportKind;
        },
        valueFormatter: (value) => {
          return prettyPrintEnumValue(value);
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.6,
        minWidth: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box>
            <Tooltip title="Go to Fulfillment">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToFulfillmentReports([params.row.id]);
                }}
              >
                <HikingIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Customer Report">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    `/o/${params.row.tenantId}/chemicals/reports/${params.row.id}`,
                    "_blank"
                  );
                }}
              >
                <DescriptionIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ];
  }, []);

  // Construct facility filter for the search query
  const facilityFilter = facilityIds.join("|");

  return (
    <Dialog
      open
      maxWidth="md"
      onClose={close}
      title="Associate Reports"
      description="Select reports that you want this mailing to be associated with. If you want the report itself to be included in the mailing, be sure to select it in the Documents section."
      primaryActionButtonProps={{
        onClick: () => saveReports(reports),
        saveText: "Done",
        hideStartIcon: true,
        loading: false,
      }}
    >
      <OmnisearchDataGrid
        tenantId={tenantId}
        persistenceKey="reports-picker"
        columns={columns}
        dataQuery={COMMUNICATION_REPORTS_QUERY}
        skip={!facilityFilter}
        initialSortModel={[
          { field: "reportingYear", sort: "desc" },
          { field: "updatedAt", sort: "desc" },
        ]}
        defaultSearch={`facilityId:${facilityFilter}`}
        getItems={(data) => data.tierIIReports.items}
        getCount={(data) => data.tierIIReports.count}
        noDataMessage="No reports found for the selected facilities."
        isURLDriven={false}
        selectedIds={reportIds}
        disableRowSelectionOnClick={false}
        withPadding={false}
        initialPageSize={10}
        onSelectedIdsChanged={(newReportIds) => {
          setReportIds(newReportIds as string[]);
        }}
        onSelectedRowsChanged={(reports) => {
          setReports(reports);
        }}
        showManageColumns={false}
      />
    </Dialog>
  );
};
